import React, { useState, useEffect } from 'react';
import { 
  UserX, 
  UserPlus, 
  RefreshCw, 
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Search,
  Filter
} from 'lucide-react';
import { FadeLoader } from 'react-spinners';
import '../styles/DeactivatedAccounts.css';

import { getAuthHeaders } from '../../../utils/authHeaders';

// Use the same API base URL as userService
const API_BASE_URL = '/api';

// Auth headers are now imported from utils/authHeaders

interface DeactivatedUser {
  _id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  deactivatedAt: string;
  deactivationReason: string;
  createdAt: string;
}

interface DeactivatedAccountsResponse {
  success: boolean;
  data: {
    users: DeactivatedUser[];
    pagination: {
      current: number;
      total: number;
      totalUsers: number;
    };
  };
  error?: string;
}

interface DeactivatedAccountsProps {
  onUserReactivated?: () => void;
}

export const DeactivatedAccounts: React.FC<DeactivatedAccountsProps> = ({ onUserReactivated }) => {
  const [deactivatedUsers, setDeactivatedUsers] = useState<DeactivatedUser[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    total: 1,
    totalUsers: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [selectedUser, setSelectedUser] = useState<DeactivatedUser | null>(null);
  const [showReactivationModal, setShowReactivationModal] = useState(false);
  const [reactivationLoading, setReactivationLoading] = useState(false);
  const [reactivationReason, setReactivationReason] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');

  useEffect(() => {
    fetchDeactivatedAccounts();
  }, [pagination.current]);

  // Fetch data when component mounts or becomes visible
  useEffect(() => {
    fetchDeactivatedAccounts();
  }, []);



  const fetchDeactivatedAccounts = async () => {
    try {
      setLoading(true);
      
      const response = await fetch(`${API_BASE_URL}/account-management/deactivated?page=${pagination.current}&limit=20`, {
        headers: getAuthHeaders(),
        credentials: 'include' // CRITICAL: Send HTTP-only auth cookie
      });
      
      const data: DeactivatedAccountsResponse = await response.json();

      if (data.success) {
        setDeactivatedUsers(data.data.users);
        setPagination(data.data.pagination);
      } else {
        setError(data.error || 'Failed to fetch deactivated accounts');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReactivation = async (user: DeactivatedUser) => {
    setSelectedUser(user);
    setShowReactivationModal(true);
  };

  const confirmReactivation = async () => {
    if (!selectedUser) return;

    try {
      setReactivationLoading(true);
      const response = await fetch(`${API_BASE_URL}/account-management/reactivate/${selectedUser._id}`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include', // CRITICAL: Send HTTP-only auth cookie
        body: JSON.stringify({
          reason: reactivationReason || 'Manual reactivation by admin',
          browserUrl: window.location.origin
        })
      });

      const data = await response.json();

      if (data.success) {
        // Remove user from list and update pagination
        setDeactivatedUsers(prev => prev.filter(u => u._id !== selectedUser._id));
        setPagination(prev => ({
          ...prev,
          totalUsers: prev.totalUsers - 1
        }));
        
        setShowReactivationModal(false);
        setSelectedUser(null);
        setReactivationReason('');
        
        // Call parent refresh if provided
        if (onUserReactivated) {
          onUserReactivated();
        }
        
        // Success message removed - user will receive email with temporary password
      } else {
        setError(data.error || 'Failed to reactivate account');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setReactivationLoading(false);
    }
  };

  const filteredUsers = deactivatedUsers.filter(user => {
    const matchesSearch = user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         `${user.firstName} ${user.lastName}`.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    return matchesSearch && matchesRole;
  });

  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, current: page }));
  };

  if (loading) {
    return (
      <div className="deactivated-accounts-container">
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '50vh',
          width: '100%'
        }}>
          <FadeLoader 
            color="#1976D2" 
            loading={true} 
            height={20} 
            width={6} 
            radius={3} 
            margin={3}
            speedMultiplier={0.8}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="deactivated-accounts-container">
      <div className="deactivated-accounts-header">
        <div className="header-content">
          <h1 className="page-title">
            <UserX className="title-icon" />
            Deactivated Accounts
          </h1>
          <p className="page-description">
            Manage accounts that have been automatically or manually deactivated
          </p>
        </div>
        
        <div className="header-actions">
          <button 
            className="btn-primary btn-auto"
            onClick={fetchDeactivatedAccounts}
            disabled={loading}
          >
            <RefreshCw className="button-icon" style={{marginRight: '8px'}} />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <AlertTriangle className="error-icon" />
          <span>{error}</span>
        </div>
      )}

      {message && (
        <div className={`message ${message.type === 'success' ? 'success' : 'error'}`}>
          <span>{message.text}</span>
        </div>
      )}

      <div className="filters-section">
        <div className="search-filter">
          <Search className="search-icon" />
          <input
            type="text"
            placeholder="Search by email or name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="role-filter">
          <Filter className="filter-icon" />
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            className="role-select"
          >
            <option value="all">All Roles</option>
            <option value="admin">Admin</option>
            <option value="manufacturer">Manufacturer</option>
            <option value="practitioner">Practitioner</option>
            <option value="insurance">Insurance</option>
          </select>
        </div>
      </div>

      <div className="accounts-summary">
        <div className="summary-card">
          <span className="summary-label">Total Deactivated</span>
          <span className="summary-value">{pagination.totalUsers}</span>
        </div>
        <div className="summary-card">
          <span className="summary-label">Current Page</span>
          <span className="summary-value">{pagination.current} of {pagination.total}</span>
        </div>
      </div>

      {filteredUsers.length === 0 ? (
        <div className="empty-state">
          <UserX className="empty-icon" />
          <h3>No deactivated accounts found</h3>
          <p>All accounts are currently active or no accounts match your filters.</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="user-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Role</th>
                <th>Status</th>
                <th>Deactivated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => (
                <tr key={user._id}>
                  <td>
                    <div>
                      <strong>{user.firstName} {user.lastName}</strong>
                      <br />
                      <small>{user.email}</small>
                    </div>
                  </td>
                  <td>
                    <span className="role-badge">
                      {user.role}
                    </span>
                  </td>
                  <td>
                    <span className="status-badge red">
                      Deactivated
                    </span>
                  </td>
                  <td>
                    <small>{new Date(user.deactivatedAt).toLocaleDateString()}</small>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="action-button reactivate"
                        onClick={() => handleReactivation(user)}
                        title="Reactivate Account"
                      >
                        <UserPlus className="button-icon" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pagination.total > 1 && (
        <div className="pagination">
          <button
            className="pagination-button"
            onClick={() => handlePageChange(pagination.current - 1)}
            disabled={pagination.current === 1}
          >
            <ChevronLeft className="button-icon" />
            Previous
          </button>
          
          <div className="pagination-info">
            Page {pagination.current} of {pagination.total}
          </div>
          
          <button
            className="pagination-button"
            onClick={() => handlePageChange(pagination.current + 1)}
            disabled={pagination.current === pagination.total}
          >
            Next
            <ChevronRight className="button-icon" />
          </button>
        </div>
      )}

      {/* Reactivation Modal */}
      {showReactivationModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowReactivationModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Reactivate Account</h3>
              <button 
                className="close-button"
                onClick={() => setShowReactivationModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <p>Are you sure you want to reactivate this account?</p>
              <div className="user-summary">
                <strong>{selectedUser.firstName} {selectedUser.lastName}</strong>
                <span>{selectedUser.email}</span>
                <span className="role-badge">{selectedUser.role}</span>
              </div>
              
              <div className="form-group">
                <label htmlFor="reactivation-reason">Reason for reactivation (optional):</label>
                <textarea
                  id="reactivation-reason"
                  value={reactivationReason}
                  onChange={(e) => setReactivationReason(e.target.value)}
                  placeholder="Enter reason for reactivation..."
                  className="reason-input"
                  rows={3}
                />
              </div>
            </div>
            
            <div className="modal-footer">
              <button
                className="modal-button cancel"
                onClick={() => setShowReactivationModal(false)}
                disabled={reactivationLoading}
              >
                Cancel
              </button>
              <button
                className="modal-button confirm"
                onClick={confirmReactivation}
                disabled={reactivationLoading}
              >
                {reactivationLoading ? (
                  <>
                    <FadeLoader 
                      color="#ffffff" 
                      loading={true} 
                      height={10} 
                      width={3} 
                      radius={1} 
                      margin={1}
                      speedMultiplier={0.8}
                    />
                    Reactivating...
                  </>
                ) : (
                  'Reactivate Account'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeactivatedAccounts;
